import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SearchIcon, CasesIcon, RulesIcon, DocIcon } from "./ShellIcons";
import { useCaseInbox } from "../../../hooks/useCases";
import { useRules } from "../../../hooks/useRules";
import { useDocumentLibrary } from "../../../hooks/useDocuments";
import {
  getRecentItems,
  addRecentItem,
  RECENT_DISPLAY_LIMIT,
  type RecentItem,
} from "../../../utils/recentItems";
import { useChatStore } from "../../../stores/chatStore";

interface CommandItem {
  kind: "Action" | "Nav" | "Case" | "Rule" | "Doc";
  label: string;
  to: string;
  /** Numeric case ID — present on Case-kind items only. */
  caseId?: number;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

type Group = {
  id: string;
  label: string;
  items: CommandItem[];
};

/**
 * Static actions + nav targets — always available regardless of data.
 */
const STATIC_ITEMS: CommandItem[] = [
  { kind: "Action", label: "Create case", to: "/cases/new" },
  { kind: "Nav", label: "Dashboard", to: "/" },
  { kind: "Nav", label: "Open Cases", to: "/cases/open" },
  { kind: "Nav", label: "Closed Cases", to: "/cases/closed" },
  { kind: "Nav", label: "Documents", to: "/healthcare/files" },
  { kind: "Nav", label: "Compliance Rules", to: "/healthcare/rules" },
];

const KIND_LIMIT = 6;

// ─── Per-type icons ────────────────────────────────────────────────────────

const ActionIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
  >
    <line x1="7" y1="2" x2="7" y2="12" />
    <line x1="2" y1="7" x2="12" y2="7" />
  </svg>
);

const NavIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon
      points="7,1 13,13 7,10 1,13"
      fill="currentColor"
      stroke="none"
      opacity="0.7"
    />
  </svg>
);

const ClockIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
  >
    <circle cx="7" cy="7" r="5.5" />
    <polyline points="7,4 7,7 9.5,9" />
  </svg>
);

const ChatIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 9.5a1 1 0 0 1-1 1H4l-2.5 2V3a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1z" />
  </svg>
);

const KIND_ICON: Record<string, React.FC<{ size?: number }>> = {
  Action: ActionIcon,
  Nav: NavIcon,
  Case: ({ size = 14 }) => <CasesIcon size={size} />,
  Rule: ({ size = 14 }) => <RulesIcon size={size} />,
  Doc: ({ size = 14 }) => <DocIcon size={size} />,
  Recent: ClockIcon,
};

// ─── Token-based fuzzy matching ────────────────────────────────────────────

/**
 * Returns a 0..1 score: fraction of query tokens found in the label.
 * Score > 0 means at least one token matched.
 */
function tokenScore(label: string, query: string): number {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length) return 1;
  const lbl = label.toLowerCase();
  return tokens.filter((t) => lbl.includes(t)).length / tokens.length;
}

/**
 * Wraps matched query tokens in a <mark> element for visual highlighting.
 */
function highlightTokens(label: string, query: string): React.ReactNode {
  const tokens = query.trim().split(/\s+/).filter(Boolean);
  if (!tokens.length) return label;
  const escaped = tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const parts = label.split(new RegExp(`(${escaped.join("|")})`, "gi"));
  const matchRe = new RegExp(`^(${escaped.join("|")})$`, "i");
  return (
    <>
      {parts.map((part, i) =>
        matchRe.test(part) ? (
          <mark
            key={i}
            style={{
              background: "var(--accent-soft, rgba(37,99,235,0.12))",
              color: "var(--accent)",
              borderRadius: 2,
              padding: "0 1px",
              fontWeight: 600,
            }}
          >
            {part}
          </mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        ),
      )}
    </>
  );
}

/** Convert a RecentItem (which has visitedAt) to a CommandItem for rendering. */
function toCommandItem(r: RecentItem): CommandItem {
  return { kind: r.kind, label: r.label, to: r.to };
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const openChat = useChatStore((s) => s.openChat);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [recentItems, setRecentItems] = useState<CommandItem[]>([]);

  // Only fetch while the palette is open — reuses dashboard cache otherwise.
  const { data: casesResponse, isLoading: casesLoading } = useCaseInbox(
    { pageSize: 200 },
    { enabled: open },
  );
  const { data: rulesResponse, isLoading: rulesLoading } = useRules(
    { size: 200 },
    { enabled: open },
  );

  // Document search — only fires when query has 2+ characters.
  const q = query.trim();
  const { data: docsResponse, isLoading: docsLoading } = useDocumentLibrary(
    { searchText: q, pageSize: KIND_LIMIT },
    { enabled: open && q.length >= 2 },
  );

  const isLoading =
    casesLoading || rulesLoading || (q.length >= 2 && docsLoading);

  // Focus input and load recent items when opened; reset on close.
  useEffect(() => {
    if (open) {
      const stored = getRecentItems().slice(0, RECENT_DISPLAY_LIMIT);
      setRecentItems(stored.map(toCommandItem));
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    setQuery("");
    setActiveIndex(0);
    return undefined;
  }, [open]);

  // Scroll the highlighted item into view whenever activeIndex changes.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(
      '[aria-selected="true"]',
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  // Build the dynamic catalog from real cases + rules.
  const allItems = useMemo<CommandItem[]>(() => {
    const cases = Array.isArray(casesResponse?.cases)
      ? casesResponse.cases
      : [];
    const rules = rulesResponse?.data?.content ?? [];

    const caseItems: CommandItem[] = cases.map((c) => {
      const ref = c.caseNumber || `CASE-${c.id}`;
      return {
        kind: "Case" as const,
        label: `${ref} — ${c.title || "Untitled case"}`,
        to: `/cases/${c.id}`,
        caseId: c.id,
      };
    });

    const ruleItems: CommandItem[] = rules.map((r) => {
      const ref = r.ruleNumber || `Rule ${r.id}`;
      return {
        kind: "Rule" as const,
        label: `${ref} — ${r.title}`,
        to: `/healthcare/rules?selected=${r.id}`,
      };
    });

    return [...STATIC_ITEMS, ...caseItems, ...ruleItems];
  }, [casesResponse, rulesResponse]);

  // Build doc items from search results.
  const docItems = useMemo<CommandItem[]>(() => {
    const files =
      docsResponse?.content ?? docsResponse?.items ?? docsResponse?.data ?? [];
    return files.slice(0, KIND_LIMIT).map((f) => ({
      kind: "Doc" as const,
      label: f.originalName || f.filename,
      to: `/healthcare/files?selected=${f.id}`,
    }));
  }, [docsResponse]);

  // Build a flat navigable list and grouped sections for rendering.
  const { filtered, groups } = useMemo<{
    filtered: CommandItem[];
    groups: Group[];
  }>(() => {
    if (q) {
      // Search mode: token-score every item, then group by kind.
      const scored = (item: CommandItem) => tokenScore(item.label, q);

      const actionMatches = STATIC_ITEMS.filter((i) => scored(i) > 0).sort(
        (a, b) => scored(b) - scored(a),
      );
      const caseMatches = allItems
        .filter((i) => i.kind === "Case" && scored(i) > 0)
        .sort((a, b) => scored(b) - scored(a))
        .slice(0, KIND_LIMIT);
      const ruleMatches = allItems
        .filter((i) => i.kind === "Rule" && scored(i) > 0)
        .sort((a, b) => scored(b) - scored(a))
        .slice(0, KIND_LIMIT);

      const grps: Group[] = [];
      if (actionMatches.length)
        grps.push({ id: "actions", label: "Actions", items: actionMatches });
      if (caseMatches.length)
        grps.push({ id: "cases", label: "Cases", items: caseMatches });
      if (ruleMatches.length)
        grps.push({ id: "rules", label: "Rules", items: ruleMatches });
      if (docItems.length)
        grps.push({ id: "docs", label: "Documents", items: docItems });

      const flat = [
        ...actionMatches,
        ...caseMatches,
        ...ruleMatches,
        ...docItems,
      ];
      return { filtered: flat, groups: grps };
    }

    // Default mode: Recent → Actions → Cases → Rules.
    const caseItems = allItems
      .filter((i) => i.kind === "Case")
      .slice(0, KIND_LIMIT);
    const ruleItems = allItems
      .filter((i) => i.kind === "Rule")
      .slice(0, KIND_LIMIT);

    const flat: CommandItem[] = [
      ...recentItems,
      ...STATIC_ITEMS,
      ...caseItems,
      ...ruleItems,
    ];

    const grps: Group[] = [];
    if (recentItems.length > 0) {
      grps.push({ id: "recent", label: "Recent", items: recentItems });
    }
    grps.push({ id: "actions", label: "Actions", items: STATIC_ITEMS });
    if (caseItems.length > 0) {
      grps.push({ id: "cases", label: "Cases", items: caseItems });
    }
    if (ruleItems.length > 0) {
      grps.push({ id: "rules", label: "Rules", items: ruleItems });
    }

    return { filtered: flat, groups: grps };
  }, [q, allItems, recentItems, docItems]);

  // Reset highlight when the filtered list changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [query, filtered.length]);

  if (!open) return null;

  const handleSelect = (item: CommandItem) => {
    addRecentItem(item);
    navigate(item.to);
    onClose();
  };

  const handleStartChat = (item: CommandItem) => {
    if (item.kind !== "Case" || !item.caseId) return;
    const caseRef = item.label.split(" — ")[0] ?? item.label;
    openChat(String(item.caseId), caseRef);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    // Ctrl+Enter → start chat for highlighted Case item
    if (
      e.key === "Enter" &&
      (e.ctrlKey || e.metaKey) &&
      filtered[activeIndex]?.kind === "Case"
    ) {
      e.preventDefault();
      handleStartChat(filtered[activeIndex]);
      return;
    }
    if (e.key === "Enter" && filtered[activeIndex]) {
      e.preventDefault();
      handleSelect(filtered[activeIndex]);
    }
  };

  const renderItem = (item: CommandItem, flatIndex: number) => {
    const isActive = flatIndex === activeIndex;
    const Icon = KIND_ICON[item.kind] ?? NavIcon;
    return (
      <button
        key={`${item.kind}-${item.to}-${item.label}`}
        type="button"
        role="option"
        aria-selected={isActive}
        onMouseEnter={() => setActiveIndex(flatIndex)}
        onClick={() => handleSelect(item)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          padding: "9px 14px",
          border: "none",
          background: isActive ? "var(--bg-sunken)" : "transparent",
          textAlign: "left",
          cursor: "pointer",
          color: "var(--ink-1)",
        }}
        className={isActive ? "cp-row cp-row--active" : "cp-row"}
      >
        <span
          aria-hidden="true"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 22,
            height: 22,
            flexShrink: 0,
            background: "var(--bg-sunken)",
            color: "var(--ink-3)",
            borderRadius: "var(--r-sm)",
            border: "1px solid var(--line)",
          }}
        >
          <Icon size={13} />
        </span>
        <span
          style={{
            fontSize: 13,
            color: "var(--ink-1)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {q ? highlightTokens(item.label, q) : item.label}
        </span>

        {/* Chat shortcut — Case rows only */}
        {item.kind === "Case" && (
          <span
            role="button"
            aria-label={`Start chat for ${item.label}`}
            title="Start chat (Ctrl+Enter)"
            onClick={(e) => {
              e.stopPropagation();
              handleStartChat(item);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 22,
              height: 22,
              flexShrink: 0,
              borderRadius: "var(--r-sm)",
              border: "1px solid var(--line)",
              background: isActive ? "var(--accent-soft)" : "var(--bg-sunken)",
              color: isActive ? "var(--accent)" : "var(--ink-3)",
              opacity: isActive ? 1 : 0,
              transition: "opacity 120ms ease, background 120ms ease",
            }}
            className="cp-chat-btn"
          >
            <ChatIcon size={12} />
          </span>
        )}
      </button>
    );
  };

  return (
    <div
      onClick={onClose}
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--scrim, rgba(15, 23, 42, 0.45))",
        zIndex: 1300,
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        data-testid="command-palette"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        style={{
          width: 640,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "70vh",
          margin: "10vh auto 0",
          background: "var(--bg-elev)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-lg, 12px)",
          boxShadow: "var(--sh-pop)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <style>{`.cp-row:hover .cp-chat-btn { opacity: 1 !important; }`}</style>
        {/* Input row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "0 14px",
            borderBottom: "1px solid var(--line)",
            color: "var(--ink-3)",
          }}
        >
          <SearchIcon size={14} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search rules, cases, docs…"
            aria-label="Search"
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              padding: 14,
              fontSize: 14,
              color: "var(--ink-1)",
              fontFamily: "inherit",
            }}
          />
        </div>

        {/* Results */}
        <div
          ref={listRef}
          role="listbox"
          aria-label="Command results"
          style={{ overflowY: "auto", padding: "4px 0", flex: 1 }}
        >
          {isLoading && filtered.length === 0 ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "24px 14px",
                color: "var(--ink-3)",
                fontSize: 13,
              }}
            >
              <svg
                aria-hidden="true"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                style={{
                  animation: "spin 0.8s linear infinite",
                }}
              >
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <path d="M7 1.5 A5.5 5.5 0 1 1 1.5 7" />
              </svg>
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div
              style={{
                padding: "24px 14px",
                textAlign: "center",
                color: "var(--ink-3)",
                fontSize: 13,
              }}
            >
              No matches for &ldquo;{query}&rdquo;
            </div>
          ) : (
            (() => {
              let flatIdx = 0;
              return groups.map((group) => (
                <React.Fragment key={group.id}>
                  <div
                    aria-hidden="true"
                    style={{
                      padding: "8px 14px 4px",
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.07em",
                      textTransform: "uppercase",
                      color: "var(--ink-3)",
                    }}
                  >
                    {group.label}
                  </div>
                  {group.items.map((item) => renderItem(item, flatIdx++))}
                </React.Fragment>
              ));
            })()
          )}
        </div>

        {/* Keyboard hint footer */}
        <div
          aria-hidden="true"
          style={{
            display: "flex",
            gap: 16,
            padding: "6px 14px",
            borderTop: "1px solid var(--line)",
            color: "var(--ink-3)",
            fontSize: 11,
          }}
        >
          <span>
            <kbd
              style={{
                fontFamily: "var(--font-mono)",
                background: "var(--bg-sunken)",
                border: "1px solid var(--line)",
                borderRadius: "var(--r-sm)",
                padding: "1px 4px",
              }}
            >
              ↑↓
            </kbd>{" "}
            navigate
          </span>
          <span>
            <kbd
              style={{
                fontFamily: "var(--font-mono)",
                background: "var(--bg-sunken)",
                border: "1px solid var(--line)",
                borderRadius: "var(--r-sm)",
                padding: "1px 4px",
              }}
            >
              ↵
            </kbd>{" "}
            open
          </span>
          <span>
            <kbd
              style={{
                fontFamily: "var(--font-mono)",
                background: "var(--bg-sunken)",
                border: "1px solid var(--line)",
                borderRadius: "var(--r-sm)",
                padding: "1px 4px",
              }}
            >
              ⌃↵
            </kbd>{" "}
            chat
          </span>
          <span>
            <kbd
              style={{
                fontFamily: "var(--font-mono)",
                background: "var(--bg-sunken)",
                border: "1px solid var(--line)",
                borderRadius: "var(--r-sm)",
                padding: "1px 4px",
              }}
            >
              Esc
            </kbd>{" "}
            close
          </span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
